// Generated by tutorials/record.cjs from tutorials/articles/h44-synced-rows-and-push-to.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h44 --text-only` (wording) or
// `node tutorials/record.cjs h44` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'synced-rows-and-push-to',
  catalogId: 'h44',
  title: 'Synced rows and Push To',
  summary: 'Keep a row the same on many days, such as the milk at every breakfast, so one change reaches all of them. Take one day out of the group when it needs to differ, and use Push To to copy one row\'s settings onto other rows once.',
  audience: 'Menu planners who build and change the menu cycle',
  area: 'menus',
  learn: [
    'What a synced row is, and how to see where else it is',
    'Sync a row to every breakfast, lunch or dinner',
    'Change the whole group with Edit Sync',
    'Take one day out of the group with Break Sync',
    'Copy one row\'s settings onto other rows with Push To',
  ],
  keywords: [
    'sync',
    'synced',
    'synced row',
    'sync group',
    'master',
    'edit sync',
    'break sync',
    'apply to group',
    'complete edit',
    'push to',
    'push',
    'copy row',
    'same every day',
    'every breakfast',
    'link',
    'locked row',
  ],
  steps: [
    {
      id: 'open',
      part: 'Sync a row to every breakfast',
      heading: 'Find a row that should be the same every day',
      text: 'A synced row is one row kept the same on many days or meals, such as MILK 2% at every breakfast. The rows in a sync group follow one of them, the master, so one change reaches all of them, and no day is forgotten.\n\nOpen the cycle and go to a day and meal with the row, here Day 1 Morning.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/open.webp',
          width: 1280,
          height: 842,
        },
        alt: 'Fall/Winter Cycle 2026 on Day 1 Morning, read only, with MILK 2% 8 fl oz in the MILK category.',
      },
    },
    {
      id: 'edit-menu',
      part: 'Sync a row to every breakfast',
      heading: 'Select Edit menu',
      text: 'The menu opens read only. Select Edit menu to make changes. Syncing changes the menu, so it needs editing on.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/edit-menu.webp',
          width: 1280,
          height: 134,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/edit-menu.mp4',
          width: 1280,
          height: 134,
          durationSec: 3.2,
        },
        alt: 'The editor toolbar showing Editing and Done after Edit menu was selected.',
      },
    },
    {
      id: 'sync-list',
      part: 'Sync a row to every breakfast',
      heading: 'Right-click the item and point at Sync',
      text: 'Right-click the item, here MILK 2%, then select Sync. The list shows each meal with the number of other rows that have the same item: Morning is every other breakfast with MILK 2%, All is every meal.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/sync-list.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/sync-list.mp4',
          width: 1280,
          height: 842,
          durationSec: 4.5,
        },
        alt: 'The right-click menu on MILK 2% with Sync open: Morning 27, All 83, Afternoon 28 and Evening 28.',
      },
    },
    {
      id: 'sync-confirm',
      part: 'Sync a row to every breakfast',
      heading: 'Pick the meal',
      text: 'Select Morning. Dietech Online asks first and says how many rows become copies of this one.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/sync-confirm.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/sync-confirm.mp4',
          width: 1280,
          height: 842,
          durationSec: 2.3,
        },
        alt: 'The question Sync MILK 2% to every Morning, saying 27 rows on 27 days become copies of this one and follow it from now on.',
      },
    },
    {
      id: 'synced',
      part: 'Sync a row to every breakfast',
      heading: 'Sync the rows',
      text: 'Select Sync 27 rows. Every breakfast\'s MILK 2% now matches this one and follows it. The link mark shows 27.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/synced.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/synced.mp4',
          width: 1280,
          height: 842,
          durationSec: 4.1,
        },
        alt: 'MILK 2% on Day 1 Morning with a link mark showing 27, and a message saying 27 rows were synced.',
      },
    },
    {
      id: 'where',
      part: 'Sync a row to every breakfast',
      heading: 'See where else it is',
      text: 'A synced row has a link mark beside its item, with the number of other rows in its group, and a lock: its cells cannot be changed by accident. Point at the link mark to see which meals and days are in the group.\n\nThe same mark is on each of the other rows.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/where.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/where.mp4',
          width: 1280,
          height: 842,
          durationSec: 2.8,
        },
        alt: 'The tooltip on the link mark of MILK 2%: Sync master, 27 other rows follow this, Morning 2 - 28.',
      },
    },
    {
      id: 'question',
      part: 'Change the whole group',
      heading: 'Start a change on the row',
      text: 'Select the synced row and press Enter, or double-click it. Because it is synced, you are asked first: Edit Sync changes every row in the group, Break Sync takes this row out and changes only it.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/question.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/question.mp4',
          width: 1280,
          height: 842,
          durationSec: 2.7,
        },
        alt: 'The question MILK 2% is synced to 27 other places, with Break Sync, Cancel and Edit Sync.',
      },
    },
    {
      id: 'edit-sync',
      part: 'Change the whole group',
      heading: 'Make the change with Edit Sync',
      text: 'Select Edit Sync, then make the change. Here the size goes from 8 fl oz to 4 fl oz. The note says where the change will go.\n\nThe bar above the grid stays until you apply the change or cancel.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/edit-sync.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/edit-sync.mp4',
          width: 1280,
          height: 842,
          durationSec: 5.8,
        },
        alt: 'The item and size editor on MILK 2% with Size 4 fl oz and a note that Apply in the bar above sends the change to Morning 2 to 28.',
      },
    },
    {
      id: 'apply',
      part: 'Change the whole group',
      heading: 'Apply it to the group',
      text: 'Select Save, then Apply to 27 rows in the bar above the grid. Dietech Online lists the rows whose settings are replaced and asks first.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/apply.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/apply.mp4',
          width: 1280,
          height: 842,
          durationSec: 4.8,
        },
        alt: 'The bar Editing a synced row: MILK 2%, and the question Apply this row to its sync group, listing Day 2 Morning to Day 7 Morning and 21 more.',
      },
    },
    {
      id: 'applied',
      part: 'Change the whole group',
      heading: 'Check another day',
      text: 'Confirm. Every breakfast in the group now serves MILK 2% at 4 fl oz. Go to any other day to check.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/applied.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/applied.mp4',
          width: 1280,
          height: 842,
          durationSec: 6,
        },
        alt: 'Day 9 Morning, with MILK 2% at 4 fl oz and the link mark.',
      },
    },
    {
      id: 'break',
      part: 'Take one day out of the group',
      heading: 'Break the sync on one row',
      text: 'When one day needs to be different, right-click its row and select Break Sync, then confirm. The row keeps its settings but stops following the group, so you can change it on its own.\n\nThe other rows stay synced.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/break.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/break.mp4',
          width: 1280,
          height: 842,
          durationSec: 4.4,
        },
        alt: 'The question Break the sync on MILK 2%, saying it keeps its current settings but stops following the other rows in its group.',
      },
    },
    {
      id: 'broken',
      part: 'Take one day out of the group',
      heading: 'See it on its own',
      text: 'Select Break sync. The link mark and the lock are gone from this row.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/broken.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/broken.mp4',
          width: 1280,
          height: 842,
          durationSec: 4,
        },
        alt: 'Day 9 Morning with MILK 2% no longer showing a link mark or a lock.',
      },
    },
    {
      id: 'push-source',
      part: 'Copy a row\'s settings with Push To',
      heading: 'Set up one row the way you want it',
      text: 'Push To copies one row\'s settings onto other rows with the same item, once. Start with the row that is right: here Renal trays should not get PEAS, so its Renal cell is clicked to an X.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/push-source.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/push-source.mp4',
          width: 1280,
          height: 842,
          durationSec: 5.8,
        },
        alt: 'Day 9 Afternoon, with PEAS in the VEGETABLE category and an X in its Renal column.',
      },
    },
    {
      id: 'push-open',
      part: 'Copy a row\'s settings with Push To',
      heading: 'Open Push To',
      text: 'With the row selected, select Tools, then Push To. The top line is the row you push from; below it, under To, are the other rows with the same item, with their day, meal and diet settings. Scroll the list to the right for the other diet columns.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/push-open.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/push-open.mp4',
          width: 1280,
          height: 842,
          durationSec: 6,
        },
        alt: 'The Push Options window: PEAS on Day 9 Afternoon at the top with an X for Renal, and the other PEAS rows on days 11, 20 and 22 below with a check mark for Renal.',
      },
    },
    {
      id: 'push-pick',
      part: 'Copy a row\'s settings with Push To',
      heading: 'Check the rows to change',
      text: 'Check each row that should match, or check the box in the heading for all of them. Push shows how many are checked.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/push-pick.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/push-pick.mp4',
          width: 1280,
          height: 842,
          durationSec: 3.9,
        },
        alt: 'All three PEAS rows checked, and the Push button showing 3.',
      },
    },
    {
      id: 'push-done',
      part: 'Copy a row\'s settings with Push To',
      heading: 'Push',
      text: 'Select Push, then Push to 3 rows. The checked rows lose their own settings and take this row\'s, so Renal trays get no PEAS on any of those days. Go to one of them to check.\n\nNothing stays linked: change the rows again later the usual way.',
      media: {
        image: {
          src: '/help/synced-rows-and-push-to/push-done.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/synced-rows-and-push-to/push-done.mp4',
          width: 1280,
          height: 842,
          durationSec: 6,
        },
        alt: 'Day 20 Afternoon, with PEAS showing an X in the Renal column after the push.',
      },
    },
  ],
  tips: [
    'Sync to another day (right-click an item) links a row to one other row with the same item, when you want two days to match but not every day.',
    'Sync with All, in the Sync list, takes every row with the item at every meal.',
    'Push To lists only rows with the same item. To put a different item on other days, change those rows, or sync them first.',
    'Hide Matching, in the Push To window, hides the rows that already match, so you see only the ones a push would change.',
  ],
  notes: [
    'Syncing and Push To change the menu, so they need Edit menu, and the right role.',
    'A synced row\'s cells are locked. A click on one asks whether to Edit Sync (change the whole group) or Break Sync (change only that row).',
    'A synced row keeps its own service item. Everything else, the item, size, diet settings, overrides and the items under it, is the same across the group.',
    'Push To is a copy, not a link: a later change to the row you pushed from does not reach the other rows.',
  ],
  related: [
    'changing-menu-items',
    'diet-and-consistency-rules',
    'reading-a-menu-cycle',
    'holiday-and-one-time-menus',
  ],
  recorded: '2026-10-26',
};

export default article;
